import {
  type FoodEntry, type Nutrients, type NutrientKey, type NutrientValue,
  NUTRIENT_META, entryNutrient, formatPortion, servingWord,
} from '../../../utils/nutrition';
import { shiftYMD } from '../../../utils/hydration';
import { LEGACY_EFFECTIVE } from '../../../utils/nutritionTargets';
import type { PlanStatus } from '../../../utils/nutritionPlan';

/** The shown form of one nutrient: "160 kcal", "P 8", or "—" when unknown. */
export function nutrientText(key: NutrientKey, v: NutrientValue, withUnit = true): string {
  const m = NUTRIENT_META[key];
  const val = v === null ? '—' : key === 'calories' ? v.toLocaleString() : String(v);
  if (key === 'calories') return withUnit ? `${val} kcal` : val;
  return `${m.short} ${val}`;
}

/** An entry's nutrients as eaten (legacy entries carry four numbers and no fibre). */
export function entryNutrients(e: FoodEntry): Nutrients {
  return {
    calories: entryNutrient(e, 'calories'),
    protein: entryNutrient(e, 'protein'),
    carbs: entryNutrient(e, 'carbs'),
    fat: entryNutrient(e, 'fat'),
    fibre: entryNutrient(e, 'fibre'),
  };
}


/** "Add ¾ serving · 390 kcal" — the primary button states its consequence. */
export function addLabel(portion: number, kcal: number | null): string {
  const what = `Add ${formatPortion(portion)} ${servingWord(portion)}`;
  return kcal === null ? what : `${what} · ${kcal.toLocaleString()} kcal`;
}

/** "Thursday, 9 October" (or "Thu, 9 Oct") for a local YYYY-MM-DD. */
export function longDate(date: string, short = false): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, short
    ? { weekday: 'short', day: 'numeric', month: 'short' }
    : { weekday: 'long', day: 'numeric', month: 'long' });
}

/** "Today", "Yesterday", or the date ("Monday, 6 October"). */
export function dayLabel(date: string, today: string, long = true): string {
  if (date === today) return 'Today';
  if (date === shiftYMD(today, -1)) return 'Yesterday';
  return longDate(date, !long);
}

/** "40 years · Male · 178 cm · 82 kg (5 Oct)" — every input the estimate uses; a missing one says so. */
export function bodySummary(b: {
  ageYears: number | null; sex: 'male' | 'female' | null; heightCm: number | null;
  weight: { value: number | null; unit: string; recordedAt: string | null; unitUnsupported: boolean };
}): string {
  const parts = [
    b.ageYears !== null ? `${b.ageYears} years` : 'birth date not set',
    b.sex ? (b.sex === 'male' ? 'Male' : 'Female') : 'gender not set',
    b.heightCm !== null ? `${Math.round(b.heightCm)} cm` : 'height not set',
    b.weight.value !== null
      ? `${b.weight.value} ${b.weight.unit}${b.weight.recordedAt ? ` (${new Date(b.weight.recordedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })})` : ''}${b.weight.unitUnsupported ? ' — unit not usable' : ''}`
      : 'no recent weight',
  ];
  return parts.join(' · ');
}

/** "from 9 October" — or "set earlier" for a version from before versions were kept. */
export function effectiveText(d: string): string {
  if (d === LEGACY_EFFECTIVE) return 'set earlier';
  const [y, m, day] = d.split('-').map(Number);
  return `from ${new Date(y, m - 1, day).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })}`;
}

const ymdDate = (date: string) => { const [y, m, d] = date.split('-').map(Number); return new Date(y, m - 1, d); };
/** "Thursday" for a local YYYY-MM-DD. */
export const weekdayName = (date: string) => ymdDate(date).toLocaleDateString(undefined, { weekday: 'long' });
/** "Thu" for a local YYYY-MM-DD. */
export const weekdayShort = (date: string) => ymdDate(date).toLocaleDateString(undefined, { weekday: 'short' });
/** "16 Oct" for a local YYYY-MM-DD. */
export const dayMonth = (date: string) => ymdDate(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
/** "1 serving", "¾ serving", "2 servings". */
export const portionText = (p: number) => `${formatPortion(p)} ${servingWord(p)}`;

/** A plan's status in words: a short chip and a line ("Day 6 of 14 · started Mon, 12 Oct · repeats"). */
export function planStatusText(days: number, s: PlanStatus): { chip: string; active: boolean; line: string } {
  const ends = (r: { repeats: boolean; endDate: string | null }) => (r.repeats || !r.endDate ? 'repeats' : `stops ${longDate(r.endDate, true)}`);
  switch (s.kind) {
    case 'active':
      return { chip: 'Active', active: true, line: `Day ${s.day} of ${days} · started ${longDate(s.run.startDate, true)} · ${s.until ? `until ${longDate(s.until, true)}` : ends(s.run)}${s.cycle > 1 ? ` · time round ${s.cycle}` : ''}` };
    case 'scheduled':
      return { chip: `Starts ${longDate(s.run.startDate, true)}`, active: false, line: `${days} days · ${ends(s.run)}` };
    case 'ended':
      return { chip: 'Not active', active: false, line: `${days} days · last ran ${longDate(s.lastDate, true)}` };
    default:
      return { chip: 'Not active', active: false, line: `${days} days · never started` };
  }
}

/** One fact about where a plan stands, for the plan's card (owner, 2026-10-21). */
export interface PlanRunFact { kind: 'started' | 'starts' | 'repeats' | 'ends' | 'until' | 'last' | 'never'; label: string; value?: string }

/**
 * ⭐ Where a plan stands, in PARTS (owner, 2026-10-21 — "find a better way" than one line of
 * "Day 3 of 7 · started Mon, Oct 5 · repeats · time round 3"): the Day it's on and which time
 * round (running only), then one or two facts — started / starts, repeats / ends / until, last
 * ran, or never started.
 */
export function planProgress(days: number, s: PlanStatus): { day: { n: number; of: number; round: number } | null; facts: PlanRunFact[] } {
  const runs = (r: { repeats: boolean; endDate: string | null }): PlanRunFact =>
    r.repeats || !r.endDate ? { kind: 'repeats', label: 'Repeats' } : { kind: 'ends', label: 'Ends', value: longDate(r.endDate, true) };
  switch (s.kind) {
    case 'active':
      return {
        day: { n: s.day, of: days, round: s.cycle },
        facts: [{ kind: 'started', label: 'Started', value: longDate(s.run.startDate, true) }, s.until ? { kind: 'until', label: 'Until', value: longDate(s.until, true) } : runs(s.run)],
      };
    case 'scheduled':
      return { day: null, facts: [{ kind: 'starts', label: 'Starts', value: longDate(s.run.startDate, true) }, runs(s.run)] };
    case 'ended':
      return { day: null, facts: [{ kind: 'last', label: 'Last ran', value: longDate(s.lastDate, true) }] };
    default:
      return { day: null, facts: [{ kind: 'never', label: 'Never started' }] };
  }
}

/** Whole-percent shares of `total` that add up to exactly 100 (largest remainder). */
export function sharesOf(parts: number[], total: number): number[] {
  if (total <= 0) return parts.map(() => 0);
  const raw = parts.map(p => (p / total) * 100);
  const out = raw.map(Math.floor);
  let left = 100 - out.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, rem: r - Math.floor(r) })).filter(x => parts[x.i] > 0).sort((a, b) => b.rem - a.rem);
  for (let j = 0; left > 0 && order.length; j = (j + 1) % order.length, left--) out[order[j].i]++;
  return out;
}
